import { useCallback, useEffect, useRef, useState } from 'react';
import type { CalendarEvent } from '../lib/api';
import { formatTime } from './dates';

export interface ReminderToast {
  id: string;
  eventId: string;
  title: string;
  body: string;
  at: number;
}

const FIRED_KEY = 'kanvas.reminders.fired.v1';
/** How often to look for due reminders. */
const TICK_MS = 20_000;
/**
 * A reminder is only worth showing for a while after it came due. Without this,
 * opening the app after a week would dump every missed reminder on screen at
 * once.
 */
const GRACE_MS = 30 * 60_000;

type FiredMap = Record<string, number>;

function loadFired(): FiredMap {
  try {
    const raw = localStorage.getItem(FIRED_KEY);
    return raw ? (JSON.parse(raw) as FiredMap) : {};
  } catch {
    return {};
  }
}

function saveFired(fired: FiredMap): void {
  try {
    // Forget entries older than a week so the record cannot grow without bound.
    const cutoff = Date.now() - 7 * 86_400_000;
    const pruned: FiredMap = {};
    for (const [key, at] of Object.entries(fired)) if (at > cutoff) pruned[key] = at;
    localStorage.setItem(FIRED_KEY, JSON.stringify(pruned));
  } catch {
    // Private mode: reminders will simply re-fire after a reload.
  }
}

/**
 * A reminder key is scoped to the event *and its start time*, so moving a
 * meeting re-arms its reminder instead of staying permanently silenced.
 */
const keyOf = (event: CalendarEvent) => `${event.id}@${event.start}`;

export interface RemindersApi {
  permission: NotificationPermission | 'unsupported';
  requestPermission: () => Promise<void>;
  toasts: ReminderToast[];
  dismiss: (id: string) => void;
}

/**
 * Fires reminders for upcoming events.
 *
 * Two delivery paths on purpose: a system notification when the user has
 * granted permission, and an in-app toast always. The toast matters because a
 * system notification is easy to miss or suppress, and because permission may
 * never be granted — a reminder that only exists behind a permission prompt is
 * not a reminder.
 *
 * This only runs while a tab is open. Genuinely reliable delivery needs a
 * service worker with push, which needs a server-side subscription store; that
 * is a deliberate later step, not something to fake here.
 */
export function useReminders(events: readonly CalendarEvent[]): RemindersApi {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    supported ? Notification.permission : 'unsupported',
  );
  const [toasts, setToasts] = useState<ReminderToast[]>([]);
  const firedRef = useRef<FiredMap>(loadFired());
  // Events change identity on every refetch; a ref keeps the interval stable.
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const requestPermission = useCallback(async () => {
    if (!supported) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }, [supported]);

  useEffect(() => {
    const check = () => {
      const now = Date.now();
      const due: ReminderToast[] = [];

      for (const event of eventsRef.current) {
        if (event.remindMinutesBefore === null) continue;
        // Nothing to be reminded of once the event is marked done.
        if (event.done) continue;
        const startAt = new Date(event.start).getTime();
        if (Number.isNaN(startAt)) continue;

        const dueAt = startAt - event.remindMinutesBefore * 60_000;
        if (now < dueAt || now > dueAt + GRACE_MS) continue;

        const key = keyOf(event);
        if (firedRef.current[key]) continue;
        firedRef.current[key] = now;

        const pending = event.tasks.filter((task) => !task.done).length;
        const when = event.allDay ? 'весь день' : `в ${formatTime(event.start)}`;
        const body = pending > 0 ? `${when} · осталось задач: ${pending}` : when;

        due.push({ id: key, eventId: event.id, title: event.title, body, at: now });
      }

      if (due.length === 0) return;
      saveFired(firedRef.current);
      setToasts((current) => [...current, ...due].slice(-4));

      if (supported && Notification.permission === 'granted') {
        for (const toast of due) {
          try {
            new Notification(toast.title, { body: toast.body, tag: toast.id });
          } catch {
            // Some browsers refuse constructed notifications outside a service
            // worker; the in-app toast already covers this case.
          }
        }
      }
    };

    check();
    const timer = window.setInterval(check, TICK_MS);
    // A tab that was in the background may have missed several ticks, so
    // re-check the moment it becomes visible again.
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [supported]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  return { permission, requestPermission, toasts, dismiss };
}
